import { useState } from "preact/hooks"
import { ChevronDown, ChevronRight, TrendingUp } from "lucide-react"

import type {
  TradeLiftCustomerRow,
  TradeLiftEventRow,
  TradeLiftSummary,
} from "../utils/trade_lift"

interface TradeLiftPanelProps {
  summary: TradeLiftSummary
  /** Caption for the window covered, e.g. "2027 plan · Jan–Dec". */
  caption?: string
  className?: string
  /** Month labels keyed by period, so the panel's columns read like the grid's. */
  period_labels?: Record<string, string>
  /**
   * `remote_promo_id` → promotion id, for events that exist in the promo calendar. Events absent
   * from this map render as plain text: the calendar year may simply not be loaded yet, and a
   * link that opens nothing is worse than no link.
   */
  promotion_ids_by_remote_id?: Record<string, string>
  /** Opens the promotion edit modal. Omit and every row stays plain text. */
  on_open_promotion?: (promotion_id: string) => void
}

function currency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0)
}

function units(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number.isFinite(value) ? value : 0)
}

function percent(value: number | null): string {
  return value == null ? "—" : `${value.toFixed(1)}%`
}

/** ROI reads as a multiple: `2.4x` of lift revenue per dollar spent. */
function roi(value: number | null): string {
  return value == null ? "—" : `${value.toFixed(2)}x`
}

/** Below this, a tactic is returning less revenue than it costs — worth colouring. */
const BREAK_EVEN_ROI = 1

function roi_class(value: number | null): string {
  if (value == null) return "text-gray-400"
  if (value < BREAK_EVEN_ROI) return "text-amber-700"
  return "text-emerald-700"
}

function Metric(props: { label: string; value: string; sub?: string; emphasis?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{props.label}</div>
      <div
        className={`tabular-nums ${props.emphasis ? "text-xl font-semibold text-gray-900" : "text-lg font-semibold text-gray-800"}`}
      >
        {props.value}
      </div>
      {props.sub ? <div className="text-[11px] leading-snug text-gray-500">{props.sub}</div> : null}
    </div>
  )
}

function EventRows(props: {
  events: TradeLiftEventRow[]
  promotion_ids_by_remote_id?: Record<string, string>
  on_open_promotion?: (promotion_id: string) => void
}) {
  if (props.events.length === 0) {
    return <div className="px-3 py-2 text-xs text-gray-500">No promotions land in the periods on screen.</div>
  }

  function promotion_id_for(event: TradeLiftEventRow): string | null {
    if (!props.on_open_promotion || !event.remote_promo_id) return null
    return props.promotion_ids_by_remote_id?.[event.remote_promo_id] ?? null
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-gray-500">
          <th class="py-1 pl-8 pr-2 text-left font-medium">Promotion</th>
          <th class="px-2 text-left font-medium">Tactic</th>
          <th class="px-2 text-left font-medium">Runs</th>
          <th class="px-2 text-left font-medium">Rate</th>
          <th class="px-2 text-right font-medium">Spend</th>
          <th class="px-2 text-right font-medium">Expected lift</th>
          <th class="py-1 pl-2 pr-3 text-right font-medium">ROI</th>
        </tr>
      </thead>
      <tbody>
        {props.events.map((event: TradeLiftEventRow) => {
          const promotion_id = promotion_id_for(event)
          return (
          <tr key={`${event.customer}:${event.id}`} class="border-t border-gray-100">
            <td class="py-1 pl-8 pr-2 text-gray-800">
              {promotion_id ? (
                <button
                  type="button"
                  class="text-left text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900 hover:decoration-blue-500"
                  title="Open this promotion in the promo calendar"
                  onClick={() => props.on_open_promotion!(promotion_id)}
                >
                  {event.name}
                </button>
              ) : (
                <span
                  title={
                    event.remote_promo_id
                      ? "Not in the promo calendar — this plan year has not been loaded into Promotions yet"
                      : "This calendar export predates promotion linking; re-import it to link these rows"
                  }
                >
                  {event.name}
                </span>
              )}
            </td>
            <td class="px-2 text-gray-600">{event.tactic}</td>
            <td class="px-2 whitespace-nowrap text-gray-500">
              {event.start} → {event.end}
            </td>
            <td class="px-2 text-gray-600">{event.rate ?? "—"}</td>
            <td class="px-2 text-right tabular-nums text-gray-800">{currency(event.spend)}</td>
            <td class="px-2 text-right tabular-nums text-gray-800">{currency(event.lift_revenue)}</td>
            <td class={`py-1 pl-2 pr-3 text-right tabular-nums ${roi_class(event.roi)}`}>{roi(event.roi)}</td>
          </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function CustomerRow(props: {
  row: TradeLiftCustomerRow
  promotion_ids_by_remote_id?: Record<string, string>
  on_open_promotion?: (promotion_id: string) => void
}) {
  const { row } = props
  const [expanded, setExpanded] = useState(false)
  const is_slice = row.group !== row.customer

  return (
    <>
      <tr class="border-t border-gray-100 hover:bg-gray-50">
        <td class="py-1.5 pl-3 pr-2">
          <button
            type="button"
            class="inline-flex items-center gap-1 text-left text-gray-800 hover:text-gray-900"
            onClick={() => setExpanded((value: boolean) => !value)}
            title={row.event_count > 0 ? "Show the promotions behind this number" : undefined}
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <span>{row.customer}</span>
          </button>
          {is_slice ? (
            <span
              class="ml-1.5 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
              title={`Planned in Confido as "${row.group}" and split across its regions by planned revenue`}
            >
              {row.group} · {row.share_pct.toFixed(0)}%
            </span>
          ) : null}
        </td>
        <td class="px-2 text-right tabular-nums text-gray-800">{currency(row.spend)}</td>
        <td class="px-2 text-right tabular-nums text-gray-800">{currency(row.lift_revenue)}</td>
        <td class="px-2 text-right tabular-nums text-gray-600">{units(row.lift_units)}</td>
        <td class={`px-2 text-right tabular-nums ${roi_class(row.roi)}`}>{roi(row.roi)}</td>
        <td class="py-1.5 pl-2 pr-3 text-right tabular-nums text-gray-500">{row.event_count}</td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={6} class="bg-gray-50/70 p-0">
            <EventRows
              events={row.events}
              promotion_ids_by_remote_id={props.promotion_ids_by_remote_id}
              on_open_promotion={props.on_open_promotion}
            />
          </td>
        </tr>
      ) : null}
    </>
  )
}

/**
 * Expected lift from trade spend, for the periods currently on screen.
 *
 * Reads only `metadata.trade_lift`, which the trade calendar importer writes and the normal
 * hierarchy save round-trips. It never derives lift from the forecast itself — the numbers here
 * are Confido's own base/incremental decomposition, so a discrepancy against the grid is a real
 * planning discrepancy rather than an artefact of this panel.
 */
export function TradeLiftPanel({
  summary,
  caption,
  className,
  period_labels,
  promotion_ids_by_remote_id,
  on_open_promotion,
}: TradeLiftPanelProps) {
  const [show_customers, setShowCustomers] = useState(false)
  const label_for = (period: string) => period_labels?.[period] ?? period

  return (
    <section
      class={`rounded-lg border border-gray-200 bg-white shadow-sm ${className ?? ""}`}
      aria-label="Expected lift from trade spend"
    >
      <header class="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div class="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-emerald-600" />
          <h3 class="text-sm font-semibold text-gray-900">Expected lift from trade spend</h3>
        </div>
        <span class="text-xs text-gray-500">
          {caption ? `${caption} · ` : ""}
          {summary.event_count} promotion{summary.event_count === 1 ? "" : "s"} across {summary.customers.length}{" "}
          customer{summary.customers.length === 1 ? "" : "s"}
        </span>
      </header>

      <div class="grid grid-cols-2 gap-4 px-4 py-3 sm:grid-cols-3 lg:grid-cols-5">
        <Metric label="Planned spend" value={currency(summary.spend)} />
        <Metric
          label="Expected lift"
          value={currency(summary.lift_revenue)}
          sub={
            summary.overlapping_revenue !== 0
              ? `${currency(summary.incremental_revenue)} on its own + ${currency(summary.overlapping_revenue)} overlapping`
              : `${units(summary.lift_units)} incremental units`
          }
          emphasis
        />
        <Metric label="Return on spend" value={roi(summary.roi)} sub="lift revenue per $1 spent" />
        <Metric
          label="Base (unpromoted)"
          value={currency(summary.base_revenue)}
          sub={`${percent(summary.lift_share_pct)} of promoted revenue is lift`}
        />
        <Metric
          label="Promoted trade rate"
          value={percent(summary.promoted_trade_rate_pct)}
          sub="spend ÷ promoted revenue"
        />
      </div>

      {summary.periods.length > 0 ? (
        <div class="overflow-x-auto border-t border-gray-100">
          <table class="w-full min-w-[44rem] text-xs">
            <thead>
              <tr class="text-gray-500">
                <th class="py-1.5 pl-4 pr-2 text-left font-medium">Period</th>
                {summary.periods.map((row) => (
                  <th key={row.period} class="px-2 text-right font-medium whitespace-nowrap">
                    {label_for(row.period)}
                  </th>
                ))}
                <th class="py-1.5 pl-2 pr-4 text-right font-semibold text-gray-700">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-t border-gray-100">
                <td class="py-1.5 pl-4 pr-2 text-gray-600">Spend</td>
                {summary.periods.map((row) => (
                  <td key={row.period} class="px-2 text-right tabular-nums text-gray-800">
                    {currency(row.spend)}
                  </td>
                ))}
                <td class="py-1.5 pl-2 pr-4 text-right font-semibold tabular-nums text-gray-900">
                  {currency(summary.spend)}
                </td>
              </tr>
              <tr class="border-t border-gray-100">
                <td class="py-1.5 pl-4 pr-2 text-gray-600">Expected lift</td>
                {summary.periods.map((row) => (
                  <td key={row.period} class="px-2 text-right tabular-nums text-gray-800">
                    {currency(row.lift_revenue)}
                  </td>
                ))}
                <td class="py-1.5 pl-2 pr-4 text-right font-semibold tabular-nums text-gray-900">
                  {currency(summary.lift_revenue)}
                </td>
              </tr>
              <tr class="border-t border-gray-100">
                <td class="py-1.5 pl-4 pr-2 text-gray-600">Lift units</td>
                {summary.periods.map((row) => (
                  <td key={row.period} class="px-2 text-right tabular-nums text-gray-600">
                    {units(row.lift_units)}
                  </td>
                ))}
                <td class="py-1.5 pl-2 pr-4 text-right font-semibold tabular-nums text-gray-700">
                  {units(summary.lift_units)}
                </td>
              </tr>
              <tr class="border-t border-gray-100">
                <td class="py-1.5 pl-4 pr-2 text-gray-600">ROI</td>
                {summary.periods.map((row) => (
                  <td key={row.period} class={`px-2 text-right tabular-nums ${roi_class(row.roi)}`}>
                    {roi(row.roi)}
                  </td>
                ))}
                <td class={`py-1.5 pl-2 pr-4 text-right font-semibold tabular-nums ${roi_class(summary.roi)}`}>
                  {roi(summary.roi)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}

      {summary.tactics.length > 0 ? (
        <div class="overflow-x-auto border-t border-gray-100 px-4 py-3">
          <div class="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">Spend by tactic</div>
          <table class="w-full min-w-[38rem] text-xs">
            <thead>
              <tr class="text-gray-500">
                <th class="w-40 pb-1 pr-3 text-left font-medium">Tactic</th>
                <th class="pb-1 text-left font-medium">
                  <span class="sr-only">Share of spend</span>
                </th>
                <th class="w-28 pb-1 pl-3 text-right font-medium" title="What this tactic is planned to cost">
                  Planned spend
                </th>
                <th
                  class="w-28 pb-1 pl-3 text-right font-medium"
                  title="The revenue Confido expects that spend to buy"
                >
                  Expected lift
                </th>
                <th class="w-16 pb-1 pl-3 text-right font-medium" title="Expected lift ÷ planned spend">
                  ROI
                </th>
              </tr>
            </thead>
            <tbody>
              {summary.tactics.map((tactic) => (
                <tr key={tactic.tactic} class="border-t border-gray-100">
                  <td class="py-1 pr-3 text-gray-700" title={tactic.tactic}>
                    <span class="block truncate">{tactic.tactic}</span>
                  </td>
                  <td class="py-1">
                    <span
                      class="block h-2 overflow-hidden rounded bg-gray-100"
                      title={`${tactic.spend_share_pct.toFixed(1)}% of planned spend`}
                    >
                      <span
                        class="block h-full rounded bg-emerald-400"
                        style={{ width: `${Math.max(1, Math.min(100, tactic.spend_share_pct))}%` }}
                      />
                    </span>
                  </td>
                  <td class="py-1 pl-3 text-right tabular-nums text-gray-800">{currency(tactic.spend)}</td>
                  <td class="py-1 pl-3 text-right tabular-nums text-gray-800">{currency(tactic.lift_revenue)}</td>
                  <td class={`py-1 pl-3 text-right tabular-nums ${roi_class(tactic.roi)}`}>{roi(tactic.roi)}</td>
                </tr>
              ))}
              <tr class="border-t border-gray-200 font-semibold text-gray-900">
                <td class="py-1 pr-3">All tactics</td>
                <td />
                <td class="py-1 pl-3 text-right tabular-nums">{currency(summary.spend)}</td>
                <td class="py-1 pl-3 text-right tabular-nums">{currency(summary.lift_revenue)}</td>
                <td class={`py-1 pl-3 text-right tabular-nums ${roi_class(summary.roi)}`}>{roi(summary.roi)}</td>
              </tr>
            </tbody>
          </table>
          <p class="mt-2 text-[11px] leading-snug text-gray-500">
            The bar is each tactic&rsquo;s share of planned spend. A tactic below <strong>1.00x</strong> is planned
            to cost more than the volume it creates — normal for fees and ads, which buy visibility rather than
            units. Expected lift is Confido&rsquo;s own attribution and includes <strong>overlapping lift</strong>{" "}
            — volume credited to a promotion that ran alongside another — so a long EDLP window picks up lift from
            the scans inside it.
          </p>
        </div>
      ) : null}

      <div class="border-t border-gray-100">
        <button
          type="button"
          class="flex w-full items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
          onClick={() => setShowCustomers((value: boolean) => !value)}
        >
          {show_customers ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          By customer ({summary.customers.length})
        </button>
        {show_customers ? (
          <div class="overflow-x-auto">
            <table class="w-full min-w-[40rem] text-xs">
              <thead>
                <tr class="text-gray-500">
                  <th class="py-1.5 pl-3 pr-2 text-left font-medium">Customer</th>
                  <th class="px-2 text-right font-medium">Spend</th>
                  <th class="px-2 text-right font-medium">Expected lift</th>
                  <th class="px-2 text-right font-medium">Lift units</th>
                  <th class="px-2 text-right font-medium">ROI</th>
                  <th class="py-1.5 pl-2 pr-3 text-right font-medium">Promos</th>
                </tr>
              </thead>
              <tbody>
                {summary.customers.map((row) => (
                  <CustomerRow
                    key={`${row.customer}:${row.group}`}
                    row={row}
                    promotion_ids_by_remote_id={promotion_ids_by_remote_id}
                    on_open_promotion={on_open_promotion}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </section>
  )
}
